import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Helmet } from 'react-helmet'
import { getBaseURL } from 'services/window'
import { extractQueryParams } from 'services/url'
import { Typography, Divider, Button, IconButton, Grid } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import PagePlaceholder from 'components/PagePlaceholder'
import PageHeader from 'components/PageHeader'
import PageHeaderTitle from 'components/PageHeaderTitle'
import PageHeaderDivider from 'components/PageHeaderDivider'
import PageHeaderActions from 'components/PageHeaderActions'
import PageContainer from 'components/PageContainer'
import PageWrapper from 'components/PageWrapper'
import PageHeaderText from 'components/PageHeaderText'
import Box from 'components/Box'
import ExternalLink from 'components/ExternalLink'
import Condition from 'components/Condition'
import Suspense from 'components/Suspense'
import ButtonStack from 'components/ButtonStack'
import StyledLink from 'components/StyledLink'
import { IUploadedVideo } from 'hooks/video'
import { Create } from '@mui/icons-material'
import { useFragment } from 'react-relay/hooks'
import { graphql } from 'react-relay'
import { useRouter } from 'next/router'
import { JobsListing_Firm$key } from '__generated__/JobsListing_Firm.graphql'
import JobsListingItem from 'components/JobsListingItem'
import { JobsListing_User$key } from '__generated__/JobsListing_User.graphql'
import { useMediaQuery } from 'hooks/useMediaQuery'
import useIsIframe from 'hooks/useIsIframe'
import AlternativeBackgroundButton from './components/AlternativeBackgroundButton/AlternativeBackgroundButton'
import useStyles from './styles'
import EmbedWidgetButton from './components/EmbedWidgetButton'
import RecordCompanyVideoButton from './components/RecordCompanyVideoButton'
import RemoveVideoButton from './components/RemoveVideoButton'
import CompanyIntroduction from './components/CompanyIntroduction'

interface IJobsListingProps {
  firmFragmentRef: JobsListing_Firm$key
  userFragmentRef: JobsListing_User$key | null
}

const JobsListing = ({ firmFragmentRef, userFragmentRef }: IJobsListingProps) => {
  const { classes } = useStyles()
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'))

  const user = useFragment(graphql`
    fragment JobsListing_User on User {
      configuration {
        enableCompanyVideos
      }
    }
  `, userFragmentRef)

  const firm = useFragment(graphql`
    fragment JobsListing_Firm on Firm {
      id
      self
      backgroundTheme
      name
      description
      slug
      logoUrl

      video {
        id
      }

      jobs(first: 20, filters: { status: opened }) {
        edges {
          node {
            id
            ...JobsListingItem_Job
          }
        }
      }
    }
  `, firmFragmentRef)

  const router = useRouter()
  const isIframe = useIsIframe()
  const isUserFirm = firm?.self
  const [video, setVideo] = useState<IUploadedVideo>()
  const showFirmControls = isUserFirm && !isIframe
  const alternativeHeader = firm?.backgroundTheme === 'light'
  const embedShowVideo = extractQueryParams(router.asPath).video === 'true' && !!video
  const embedShowText = extractQueryParams(router.asPath).text === 'true'
  const noLayout = extractQueryParams(router.asPath).layout === 'false' // && embedMode === 'compact'
  const subtitleClasses = alternativeHeader ? [classes.subtitle] : [classes.subtitle, classes.subtitleNotAlternative]
  const showRecordVideoButton = user?.configuration?.enableCompanyVideos
  const showRemoveVideoButton = video && showRecordVideoButton
  const [limitItems, setLimitItems] = useState(isSmallScreen && noLayout)

  const jobs = useMemo(() => {
    return firm?.jobs?.edges?.map(e => e.node)?.filter(n => Boolean(n)) || []
  }, [firm?.jobs])

  const onVideoRecorded = useCallback((v: IUploadedVideo) => { setVideo(v) }, [setVideo])
  const onVideoRemoved = useCallback(() => { setVideo(null) }, [setVideo])

  useEffect(() => { if (firm?.video) setVideo(firm?.video) }, [firm?.video])

  let title = `Careers at ${firm.name}`
  if (title !== 'Careers at Flexhire') title += ' - Flexhire'

  let description = (firm.description || '').substring(0, 150)

  if (!description) {
    if (jobs.length > 0) {
      description = `${jobs.length} Open Position${jobs.length > 1 ? 's' : ''}`
    } else {
      description = 'No open positions at the moment'
    }
  }

  return (
    <React.Fragment>
      <Helmet titleTemplate="%s">
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:url" content={`${getBaseURL()}/${firm?.slug}`} />
        <meta property="og:description" content={description} />
        {firm?.logoUrl && <meta property="og:image" content={firm.logoUrl} />}
      </Helmet>

      {!noLayout && (
        <PageHeader alternative={alternativeHeader} compact={jobs.length < 2}>
          <PageHeaderTitle className={classes.title}>
            {firm?.logoUrl && (
              <div className={classes.logoContainer}>
                <div className={classes.logoWrapper}>
                  <img
                    src={firm.logoUrl}
                    className={classes.logo}
                    alt={`${firm?.name} logo`}
                    style={firm?.backgroundTheme === 'light' ? { boxShadow: `0 0 32px 16px ${'#FFF'}` } : undefined}
                  />
                </div>
              </div>
            )}

            Available Jobs at {firm.name}

            <Typography variant="body1" className={subtitleClasses.join(' ')} component="div">
              {jobs.length || 'No'} open job posting{jobs.length === 1 ? '' : 's'}
            </Typography>
          </PageHeaderTitle>

          {(showFirmControls || firm.description) && (
            <PageHeaderDivider />
          )}

          {showFirmControls && (
            <PageHeaderActions alternative={alternativeHeader}>
              <ButtonStack style={{ margin: '0 auto' }}>
                <Condition condition={isSmallScreen}>
                  <IconButton
                    component={StyledLink}
                    href="/hire/jobs"
                    style={{ textDecoration: 'none' }}
                    data-cy="edit-jobs-listing"
                  >
                    <Create />
                  </IconButton>
                </Condition>

                <Condition condition={!isSmallScreen}>
                  <Button
                    color="primary"
                    component={StyledLink}
                    href="/hire/jobs"
                    style={{ textDecoration: 'none' }}
                    data-cy="edit-jobs-listing"
                    startIcon={<Create />}
                  >
                    Edit your jobs listing
                  </Button>
                </Condition>

                <Suspense>
                  <EmbedWidgetButton
                    firmSlug={firm?.slug}
                    firmVideo={video}
                    iconOnly={isSmallScreen}
                  />
                </Suspense>

                {showRecordVideoButton && firm?.id && (
                  <Suspense>
                    <RecordCompanyVideoButton
                      firmId={firm?.id}
                      onVideoRecorded={onVideoRecorded}
                      iconOnly={isSmallScreen}
                    />
                  </Suspense>
                )}

                {showRemoveVideoButton && (
                  <RemoveVideoButton
                    firm={firm}
                    video={video}
                    onVideoRemoved={onVideoRemoved}
                    iconOnly={isSmallScreen}
                  />
                )}

                <AlternativeBackgroundButton
                  firm={firm}
                />
              </ButtonStack>
            </PageHeaderActions>
          )}

          {firm.description && (
            <PageHeaderText>
              <CompanyIntroduction firm={firm} video={video} />
            </PageHeaderText>
          )}
        </PageHeader>
      )}

      <PageContainer>
        <PageWrapper raised withoutCard>
          {(noLayout && (embedShowVideo || embedShowText)) && (
            <React.Fragment>
              <Box>
                <Typography variant="body1">
                  <CompanyIntroduction
                    firm={firm}
                    video={video}
                    hideText={!embedShowText}
                    hideVideo={!embedShowVideo}
                  />
                </Typography>
              </Box>

              <Divider style={{ margin: 0 }} />
            </React.Fragment>
          )}

          {jobs.length === 0 && (
            <PagePlaceholder
              raised
              title="No open positions"
              subtitle={`${firm.name} is not hiring at the moment`}
            />
          )}

          <Condition condition={jobs.length > 0}>
            <Grid container spacing={2} data-cy="jobs-list">
              {jobs.slice(0, Math.min(2, jobs.length)).map(job => (
                <Grid item xs={12} key={job.id}>
                  <JobsListingItem jobFragmentRef={job} disableCompanyName disableApplicationSteps raised variant="elevation" />
                </Grid>
              ))}

              <Condition condition={jobs.length > 2 && !limitItems}>
                {jobs.slice(2, jobs.length).map(job => (
                  <Grid item xs={12} key={job.id}>
                    <JobsListingItem jobFragmentRef={job} disableCompanyName disableApplicationSteps raised variant="elevation" />
                  </Grid>
                ))}
              </Condition>
            </Grid>

            <Condition condition={limitItems}>
              <Box>
                <Button color="secondary" fullWidth onClick={() => setLimitItems(false)}>
                  Show More Jobs
                </Button>
              </Box>
            </Condition>
          </Condition>

          {noLayout && (
            <div className={classes.link}>
              <Typography variant="subtitle2">
                Powered by <ExternalLink href={getBaseURL()} label="FlexHire" />
              </Typography>
            </div>
          )}
        </PageWrapper>
      </PageContainer>
    </React.Fragment>
  )
}

export default JobsListing
